/**
 * Gemeinsamer Kontext der Staffing-Maske (#158): Projekt, Rechte, Auswahllisten und das
 * Neuladen nach jeder Änderung. Liegt in einer eigenen Datei, damit Zeilen-Komponenten
 * ihn nutzen können, ohne `staffing-maske.tsx` zu importieren (kein Import-Kreis).
 */
import type { PreisEinheit } from '@projektverwaltung/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../toast.js';

export type MaskeRechte = { canResolve: boolean; canManage: boolean; canAusplanen: boolean };

export type MaskeOption = { value: string; label: string };

export type MaskeKontextWert = {
  projectId: string;
  rechte: MaskeRechte;
  /** Bestätigte Positionen als Auswahl (Besetzung umhängen, „Position zuordnen…"). */
  positionOptionen: MaskeOption[];
  /** Aktive, nicht mit Salesforce verknüpfte Besetzungen („mit Besetzung verknüpfen…"). */
  verknuepfbareBesetzungen: MaskeOption[];
};

export const MaskeKontext = createContext<MaskeKontextWert | null>(null);

export function useMaske(): MaskeKontextWert {
  const wert = useContext(MaskeKontext);
  if (!wert) throw new Error('useMaske ausserhalb von <StaffingMaske>');
  return wert;
}

/**
 * Query-Keys, die nach jeder Änderung neu geladen werden: Maske, KL-Board und — wie
 * früher `StaffingInline` — alles, was das vom Server neu berechnete Flag `staffing_needed`
 * zeigt (Projekt, Mission Control, Phase Board, Atlas).
 */
export function maskeQueryKeys(projectId: string): readonly (readonly string[])[] {
  return [
    ['staffing-maske', projectId],
    ['v2-kl-staffing'],
    ['v2-project', projectId],
    ['v2-mc-projects'],
    ['v2-board-projects'],
    ['v2-atlas-projects', 'all'],
  ];
}

/**
 * Führt einen Schreibaufruf aus und lädt danach Maske und KL-Board neu. Wirft den Fehler weiter
 * (für `InlineFeld`, das selbst meldet und den alten Wert zurückholt).
 */
export function useMaskeSchreiben(): (aufruf: () => Promise<unknown>) => Promise<void> {
  const { projectId } = useMaske();
  const qc = useQueryClient();
  return useCallback(
    async (aufruf) => {
      await aufruf();
      await Promise.all(
        maskeQueryKeys(projectId).map((k) => qc.invalidateQueries({ queryKey: [...k] })),
      );
    },
    [projectId, qc],
  );
}

/** Wie `useMaskeSchreiben`, aber für Knöpfe: Fehler landen als `toast.error`. */
export function useMaskeKnopf(): (aufruf: () => Promise<unknown>) => void {
  const schreiben = useMaskeSchreiben();
  const toast = useToast();
  const { t } = useTranslation();
  return useCallback(
    (aufruf) => {
      schreiben(aufruf).catch((e: unknown) =>
        toast.error(
          t('v2.staffing_maske.aktion_fehlgeschlagen', 'Aktion fehlgeschlagen'),
          e instanceof Error ? e.message : undefined,
        ),
      );
    },
    [schreiben, toast, t],
  );
}

/** Wirft bei leerem Pflichtwert — `InlineFeld` zeigt dann den Toast und den alten Wert. */
export function usePflicht(): <T>(neu: T | null) => T {
  const { t } = useTranslation();
  return <T,>(neu: T | null): T => {
    if (neu === null) {
      throw new Error(t('v2.staffing_maske.pflichtfeld', 'Pflichtfeld — darf nicht leer sein.'));
    }
    return neu;
  };
}

export function usePreisText(): (preis: number | null, einheit: PreisEinheit) => string {
  const { t } = useTranslation();
  // Stabil je Sprache: die Maske baut damit ihre Positionsauswahl in einem useMemo (F1).
  return useCallback(
    (preis, einheit) => {
      if (preis === null) return '—';
      const suffix =
        einheit === 'tag'
          ? t('v2.staffing_maske.einheit_tag', '€ /Tag')
          : t('v2.staffing_maske.einheit_stunde', '€ /h');
      return `${preis.toLocaleString('de-DE')} ${suffix}`;
    },
    [t],
  );
}
